// The Studio: who is in it, what each person holds, and how the weight sits.
// Roles save the moment they are pressed. Behaviour lives in web/docs/features/spark.md.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useToast } from '../components/Toast/ToastContext';
import Appear from '../components/ui/Appear';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import { Avatar } from '../components/ui/Avatar';
import { IconButton } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { IconArrowUpRight, IconClose, IconPlus, IconSpark } from '../components/ui/icons';
import { PageLayout } from '../components/ui/PageLayout';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { Select } from '../components/ui/Select';
import { Skeleton } from '../components/ui/Skeleton';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { Tooltip } from '../components/ui/Tooltip';
import { useTicker } from '../components/ui/motion';
import useUserStore from '../stores/useUserStore';
import { liveScope, useLiveScopes } from '../live/useLiveScopes';
import { getWorkload, getYear, listPeople, setRoles } from './sparkApi';
import type { Person } from './sparkApi';
import { RESOURCES, STUDIO_ROLES } from './sparkRules';
import type { StudioRole } from './sparkRules';

type Workload = Awaited<ReturnType<typeof getWorkload>>['workload'];

const roleLabel = (key: StudioRole) => STUDIO_ROLES.find((r) => r.key === key)?.label ?? key;

const apiError = (err: unknown): { status?: number; code?: string; message?: string } => {
  const e = err as { status?: number; data?: { error?: { code?: string; message?: string } | string } } | null;
  const d = e?.data?.error;
  if (typeof d === 'string') return { status: e?.status, code: d, message: d };
  return { status: e?.status, code: d?.code, message: d?.message };
};

/** Roles are not categories with colours of their own: one quiet grey tag. */
const ROLE_HEX = CATEGORY.neutral.ink.hex;

function Retry({ children, onRetry }: { children: React.ReactNode; onRetry: () => void }) {
  return (
    <Alert tone="error" onRetry={onRetry}>{children}</Alert>
  );
}

interface RowProps {
  person: Person;
  editable: boolean;
  busy: boolean;
  /** Roles nobody else holds: removing one would leave the Studio without it. */
  soleDirector: boolean;
  onChange: (person: Person, roles: StudioRole[]) => void;
}

function RosterRow({ person, editable, busy, soleDirector, onChange }: RowProps) {
  const missing = STUDIO_ROLES.filter((r) => !person.roles.includes(r.key));

  return (
    <li className="flex min-w-0 items-start gap-2.5 rounded-panel bg-surface-container px-2.5 py-2">
      <Avatar name={person.name} userId={person.id} color={person.avatar_color} shape={person.avatar_shape} accessory={person.avatar_accessory} size="sm" ring="ring-surface-container" decorative className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-semibold leading-6 text-on-surface">{person.name}</span>
          {person.approver && <span className="flex-none text-xs text-on-surface-variant">Approver</span>}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {/* Every role in one fixed order, so a role that is added or removed grows or fades in its own place. */}
          {STUDIO_ROLES.map(({ key: role }) => {
            const held = person.roles.includes(role);
            const last = person.roles.length <= 1;
            const lastDirector = role === 'director' && soleDirector;
            const blocked = last || lastDirector;
            const why = last
              ? 'Everyone keeps one role. Add another before removing this one.'
              : lastDirector
                ? 'Spark needs a Director. Make someone else one first.'
                : undefined;
            return (
              <Appear key={role} show={held} className="inline-flex">
                <span className="inline-flex items-center rounded-full bg-surface-container-high pl-0.5">
                  <Tag hex={ROLE_HEX} className="!bg-transparent">{roleLabel(role)}</Tag>
                  {editable && (
                    <Tooltip label={`Remove ${roleLabel(role)} from ${person.name}`} note={why}>
                      <IconButton
                        size="sm"
                        label={`Remove ${roleLabel(role)} from ${person.name}`}
                        data-role-remove={`${person.id}:${role}`}
                        disabled={busy || blocked}
                        onClick={() => onChange(person, person.roles.filter((r) => r !== role))}
                        className="!h-6 !w-6 !rounded-full"
                      >
                        <IconClose className="h-3 w-3" />
                      </IconButton>
                    </Tooltip>
                  )}
                </span>
              </Appear>
            );
          })}
          <Appear show={editable && missing.length > 0} className="inline-flex">
            <Tooltip label="Add a role" note="It saves the moment you choose one.">
              <Select
                variant="inline"
                label={`Add a role for ${person.name}`}
                value={null}
                placeholder="choose one"
                options={missing.map((r) => ({ key: r.key, label: r.label }))}
                onChange={(key) => onChange(person, [...person.roles, key as StudioRole])}
                disabled={busy}
                renderValue={() => <IconPlus className="h-3.5 w-3.5" />}
                className="!mx-0 h-6 !rounded-full bg-surface-container-high !px-1.5 text-on-surface-variant hover:text-on-surface"
              />
            </Tooltip>
          </Appear>
        </div>
      </div>
    </li>
  );
}

/** A bar that springs out from zero once the workload has loaded; heavy from 3/4 of the top load. */
function WorkloadRow({ name, spice, projects, max, index, ready }: { name: string; spice: number; projects: number; max: number; index: number; ready: boolean }) {
  const value = ready ? spice : 0;
  const shown = useTicker(value);
  const heavyAt = max * 0.75;
  const heavy = spice > 0 && spice >= heavyAt;
  return (
    <li className="grid grid-cols-[minmax(7rem,12rem)_minmax(0,1fr)_auto] items-center gap-3 text-sm">
      <span className={heavy ? 'font-semibold text-on-surface' : 'text-on-surface-variant'}>{name}</span>
      <Progress
        value={value}
        max={max}
        tone="neutral"
        label={`${name}'s load`}
        valueText={`spice ${spice} across ${projects} ${projects === 1 ? 'project' : 'projects'}`}
        warn={{ at: heavyAt, label: 'Heavy' }}
        delayMs={ready ? index * 40 : 0}
      />
      <span aria-hidden="true" className="w-6 text-right font-mono text-xs tabular-nums text-on-surface">{shown}</span>
    </li>
  );
}

const SparkStudio: React.FC = () => {
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const approver = !!(user?.isadmin || user?.can_admin || (user?.can_animator && user?.can_manager));

  const [people, setPeople] = useState<Person[] | null>(null);
  const [peopleError, setPeopleError] = useState(false);
  const [workload, setWorkload] = useState<Workload | null>(null);
  const [workloadError, setWorkloadError] = useState(false);
  const [year, setYear] = useState<{ year: number; flown: number } | null>(null);
  const [yearError, setYearError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadPeople = useCallback(async () => {
    setPeopleError(false);
    try { setPeople((await listPeople()).people); } catch { setPeopleError(true); }
  }, []);
  const loadWorkload = useCallback(async () => {
    setWorkloadError(false);
    try { setWorkload((await getWorkload()).workload); } catch { setWorkloadError(true); }
  }, []);
  const loadYear = useCallback(async () => {
    setYearError(false);
    try { setYear(await getYear()); } catch { setYearError(true); }
  }, []);

  useEffect(() => {
    loadPeople();
    loadYear();
    if (approver) loadWorkload();
  }, [loadPeople, loadYear, loadWorkload, approver]);

  // Someone else's change: refetch quietly. A failure keeps what is on screen rather than raising
  // Retry; the rejection tells the poller to try again.
  const { holdWhile } = useLiveScopes([liveScope.spark], (_changed, { stale }) => Promise.all([
    listPeople().then((r) => { if (!stale()) setPeople(r.people); }),
    getYear().then(setYear),
    approver ? getWorkload().then((r) => setWorkload(r.workload)) : undefined,
  ]));

  const roster = useMemo(() => (people ?? []).filter((p) => p.studio), [people]);
  const directors = roster.filter((p) => p.roles.includes('director')).length;

  const changeRoles = async (person: Person, roles: StudioRole[]) => {
    if (roles.length === 0) {
      toast.error('Everyone keeps one role. Add another before removing this one.');
      return;
    }
    setBusyId(person.id);
    try {
      const res = await holdWhile(setRoles(person.id, roles));
      setPeople((prev) => (prev ?? []).map((p) => (p.id === person.id ? { ...p, roles: res.roles } : p)));
      toast.success(`${person.name} now holds ${res.roles.map(roleLabel).join(', ')}.`);
    } catch (err) {
      const { status, code, message } = apiError(err);
      if (status === 409 && code === 'last_director') {
        toast.error('Spark needs at least one Director. Make someone else a Director first.');
      } else {
        toast.error(message || 'That role change did not save. Try again.');
      }
      loadPeople();
    }
    setBusyId(null);
  };

  const topSpice = Math.max(1, ...(workload ?? []).map((w) => w.spice));
  const rows = [...(workload ?? [])].sort((a, b) => b.spice - a.spice);

  const [barsReady, setBarsReady] = useState(false);
  useEffect(() => {
    if (!workload) return undefined;
    // Two frames so the empty bars paint once, then spring out.
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setBarsReady(true)); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, [workload]);

  const header = (
    <PageHeader
      title="Studio"
      icon={<IconSpark />}
      lede="Who is in the Creative Studio, what each person does, and how the weight sits."
      back={{ to: '/spark', label: 'Spark' }}
    />
  );

  return (
    <PageLayout width="full" kind="workspace" header={header}>
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Panel
          title="Roster"
          actions={people ? <span className="text-xs tabular-nums text-on-surface-variant">{roster.length} {roster.length === 1 ? 'person' : 'people'}, one or more roles each</span> : undefined}
        >
          {peopleError && !people ? (
            <FadeIn key="error"><Retry onRetry={loadPeople}>The roster did not load. Check your connection and try again.</Retry></FadeIn>
          ) : !people ? (
            <div role="status" aria-label="Loading the roster" className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))] gap-2">
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[3.75rem]" />)}
            </div>
          ) : roster.length === 0 ? (
            <FadeIn key="empty">
              <EmptyState variant="area">
                No one is on the Studio roster yet. Give a person a Studio role from the user admin page and they appear here.
              </EmptyState>
            </FadeIn>
          ) : (
            <FadeIn key="roster">
              <p className="m-0 mb-3 max-w-prose text-sm text-on-surface-variant">
                {approver
                  ? 'Remove a role with its cross, or add one with the plus beside the tags. Each change saves the moment you make it. Everyone keeps at least one role, and Spark always needs a Director.'
                  : 'Only approvers can change roles.'}
              </p>
              <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))] gap-2 p-0">
                {roster.map((p) => (
                  <RosterRow
                    key={p.id}
                    person={p}
                    editable={approver}
                    busy={busyId === p.id}
                    soleDirector={directors <= 1}
                    onChange={changeRoles}
                  />
                ))}
              </ul>
              <p className="m-0 mt-3 max-w-prose text-xs text-on-surface-variant">
                Designers are the pool a show team is drawn from, and the first reviewer on a show. Directors are asked for Director Review. Shows already drawn keep their designer.
              </p>
            </FadeIn>
          )}
        </Panel>

        <div className="min-w-0 space-y-4">
          <Panel title="Workload" actions={<span className="text-xs text-on-surface-variant">In spice, for approvers</span>}>
            {!approver ? (
              <p className="m-0 text-sm text-on-surface-variant">
                <Tag hex={ROLE_HEX} className="mr-2">Managers only</Tag>
                The workload view shows spice, never hours.
              </p>
            ) : workloadError && !workload ? (
              <FadeIn key="error"><Retry onRetry={loadWorkload}>The workload did not load. Try again in a moment.</Retry></FadeIn>
            ) : !workload ? (
              <div role="status" aria-label="Loading the workload" className="space-y-2">
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-6" />)}
              </div>
            ) : rows.length === 0 ? (
              <FadeIn key="empty"><EmptyState variant="area">No one has open projects yet. Weight shows here once a project has a team.</EmptyState></FadeIn>
            ) : (
              <FadeIn key="rows">
                <ul className="m-0 list-none space-y-2.5 p-0">
                  {rows.map((w, i) => (
                    <WorkloadRow key={w.id} name={w.name} spice={w.spice} projects={w.projects} max={topSpice} index={i} ready={barsReady} />
                  ))}
                </ul>
                <p className="m-0 mt-3 text-xs text-on-surface-variant">
                  Spice of the open projects each person answers for or animates on. Never hours. Reviews are not counted, and the bars are scaled to the heaviest load.
                </p>
              </FadeIn>
            )}
          </Panel>

          <Panel title="The year, counted">
            {yearError && !year ? (
              <FadeIn key="error"><Retry onRetry={loadYear}>The count did not load. Try again in a moment.</Retry></FadeIn>
            ) : !year ? (
              <div role="status" aria-label="Loading the count"><Skeleton className="h-12 w-40" /></div>
            ) : (
              <FadeIn key="count"><YearCount year={year.year} flown={year.flown} /></FadeIn>
            )}
          </Panel>

          <Panel title="Resources" actions={<span className="text-xs text-on-surface-variant">From the Studio's GitBook</span>}>
            <ul className="m-0 list-none p-0">
              {RESOURCES.map((r) => {
                const body = (
                  <>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{r.title}</span>
                      <span className="block text-xs text-on-surface-variant">{r.summary}</span>
                    </span>
                    {r.url ? <IconArrowUpRight className="h-4 w-4 flex-none text-on-surface-variant" /> : <span className="flex-none text-xs">Not yet linked</span>}
                  </>
                );
                return (
                  <li key={r.title}>
                    {r.url ? (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="np-state flex items-center justify-between gap-3 rounded-control px-2 py-2 text-on-surface transition-[background-color,--np-layer] duration-quick ease-emphasized focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      >
                        {body}
                      </a>
                    ) : (
                      <div className="flex items-center justify-between gap-3 px-2 py-2 text-on-surface-variant">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Panel>
        </div>
      </div>
    </PageLayout>
  );
};

function YearCount({ year, flown }: { year: number; flown: number }) {
  // Counts up from nothing the first time it appears.
  const [target, setTarget] = useState(0);
  useEffect(() => { setTarget(flown); }, [flown]);
  const shown = useTicker(target, 900);
  return (
    <>
      <div className="font-mono text-5xl font-extrabold leading-none tabular-nums text-on-surface">{shown}</div>
      <p className="m-0 mt-2 text-sm text-on-surface-variant">
        {flown === 1 ? 'show' : 'shows'} flown in {year}
      </p>
    </>
  );
}

export default SparkStudio;
